import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "./Procedure.css";
import { API_BASE } from "../apiConfig";

function actorName() {
  return (
    localStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    localStorage.getItem("userRole") ||
    "User"
  );
}

function todayIndia() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(new Date());
}

function fmtDate(value) {
  if (!value) return "-";
  const d = new Date(`${value}T00:00:00+05:30`);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).format(d);
}

async function readJson(response) {
  const text = await response.text();
  if (!text.trim()) {
    throw new Error(`Empty response. HTTP ${response.status}`);
  }
  let result;
  try {
    result = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON: ${text.substring(0, 300)}`);
  }
  if (!response.ok || result.status !== "success") {
    throw new Error(result.msg || result.message || `HTTP ${response.status}`);
  }
  return result;
}

export default function PatientProcedureHistory({
  patientId,
  heading = "Procedures / Follow-up",
  activeOnly = false,
  focusProcedureId = null,
  canEdit = true,
  canComplete = true,
  showHeading = true,
  refreshKey = 0,
  onChanged
}) {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    total_days: "",
    procedure_details: "",
    start_date: ""
  });
  const [sessionDrafts, setSessionDrafts] = useState({});

  const loadProcedures = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}get_patient_procedures.php?patient_id=${encodeURIComponent(patientId)}`
      );
      const result = await readJson(response);
      setProcedures(Array.isArray(result.data) ? result.data : []);

      const drafts = {};
      (result.data || []).forEach((plan) => {
        (plan.sessions || []).forEach((session) => {
          drafts[session.id] = {
            actual_date: session.actual_date || session.scheduled_date || todayIndia(),
            completion_details: session.completion_details || ""
          };
        });
      });
      setSessionDrafts(drafts);
    } catch (error) {
      console.error("Procedure history load error:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to Load Procedures",
        text: error.message
      });
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadProcedures();
  }, [loadProcedures, refreshKey]);

  const visible = useMemo(() => {
    let rows = procedures;
    if (activeOnly) rows = rows.filter((p) => p.status === "active");
    if (focusProcedureId) {
      const focus = rows.find((p) => Number(p.id) === Number(focusProcedureId));
      if (focus) {
        rows = [focus, ...rows.filter((p) => Number(p.id) !== Number(focusProcedureId))];
      }
    }
    return rows;
  }, [procedures, activeOnly, focusProcedureId]);

  const pendingTotal = useMemo(
    () => procedures.reduce((sum, p) => sum + Number(p.pending_count || 0), 0),
    [procedures]
  );

  const notifyChanged = async () => {
    await loadProcedures();
    if (onChanged) onChanged();
  };

  const beginEdit = (plan) => {
    setEditingId(plan.id);
    setEditForm({
      total_days: String(plan.total_days || ""),
      procedure_details: plan.procedure_details || "",
      start_date: plan.start_date || ""
    });
  };

  const saveEdit = async (plan) => {
    if (
      !String(editForm.total_days).trim() ||
      !editForm.procedure_details.trim() ||
      !editForm.start_date
    ) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Procedure",
        text: "Number of days, procedure details and start date are required."
      });
      return;
    }

    try {
      const response = await fetch(`${API_BASE}update_procedure.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          procedure_id: Number(plan.id),
          total_days: Number(editForm.total_days),
          procedure_details: editForm.procedure_details.trim(),
          start_date: editForm.start_date
        })
      });
      await readJson(response);
      setEditingId(null);
      await notifyChanged();
      Swal.fire({
        icon: "success",
        title: "Procedure Updated",
        timer: 1100,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Update Failed", text: error.message });
    }
  };

  const cancelPlan = async (plan) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Cancel this procedure?",
      html: `<strong>${plan.procedure_details || "Procedure"}</strong><br><br>All pending days under this procedure will be cancelled.`,
      input: "textarea",
      inputLabel: "Reason for cancellation",
      inputPlaceholder: "Enter reason...",
      inputAttributes: { "aria-label": "Cancellation reason" },
      showCancelButton: true,
      confirmButtonText: "Cancel Procedure",
      confirmButtonColor: "#d33",
      preConfirm: (value) => {
        if (!String(value || "").trim()) {
          Swal.showValidationMessage("Cancellation reason is required.");
          return false;
        }
        return String(value).trim();
      }
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${API_BASE}cancel_procedure.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          procedure_id: Number(plan.id),
          reason: result.value,
          cancelled_by: actorName()
        })
      });
      await readJson(response);
      await notifyChanged();
      Swal.fire({
        icon: "success",
        title: "Procedure Cancelled",
        timer: 1100,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Cancellation Failed", text: error.message });
    }
  };

  const updateSessionDraft = (sessionId, field, value) => {
    setSessionDrafts((prev) => ({
      ...prev,
      [sessionId]: {
        ...(prev[sessionId] || {}),
        [field]: value
      }
    }));
  };

  const completeSession = async (session) => {
    const draft = sessionDrafts[session.id] || {};
    if (!draft.actual_date || !String(draft.completion_details || "").trim()) {
      Swal.fire({
        icon: "warning",
        title: "Enter Procedure Details",
        text: "Completion date and details of what was done are required."
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: "question",
      title: `Complete Day ${session.day_no}?`,
      text: "After completion this row becomes read-only.",
      showCancelButton: true,
      confirmButtonText: "Yes, Complete",
      confirmButtonColor: "#198754"
    });
    if (!confirm.isConfirmed) return;

    try {
      const response = await fetch(`${API_BASE}complete_procedure_session.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: Number(session.id),
          actual_date: draft.actual_date,
          completion_details: draft.completion_details.trim(),
          completed_by: actorName()
        })
      });
      await readJson(response);
      await notifyChanged();
      Swal.fire({
        icon: "success",
        title: "Procedure Day Completed",
        timer: 1000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Unable to Complete", text: error.message });
    }
  };

  const cancelSession = async (session) => {
    const result = await Swal.fire({
      icon: "warning",
      title: `Cancel Day ${session.day_no}?`,
      input: "textarea",
      inputLabel: "Reason for cancellation",
      inputPlaceholder: "Enter reason...",
      showCancelButton: true,
      confirmButtonText: "Cancel This Day",
      confirmButtonColor: "#d33",
      preConfirm: (value) => {
        if (!String(value || "").trim()) {
          Swal.showValidationMessage("Cancellation reason is required.");
          return false;
        }
        return String(value).trim();
      }
    });
    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${API_BASE}cancel_procedure_session.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: Number(session.id),
          reason: result.value,
          cancelled_by: actorName()
        })
      });
      await readJson(response);
      await notifyChanged();
    } catch (error) {
      Swal.fire({ icon: "error", title: "Cancellation Failed", text: error.message });
    }
  };

  return (
    <div className="procedure-history">
      {showHeading && (
        <div className="procedure-history-heading">
          <div>
            <i className="fa fa-medkit"></i> {heading}
          </div>
          <span className={pendingTotal ? "procedure-pending-badge" : "procedure-zero-badge"}>
            {pendingTotal} Pending
          </span>
        </div>
      )}

      {loading ? (
        <div className="procedure-loading">
          <i className="fa fa-spinner fa-spin"></i> Loading procedures...
        </div>
      ) : visible.length === 0 ? (
        <div className="procedure-empty">
          <i className="fa fa-check-circle"></i>
          <span>No procedure records found.</span>
        </div>
      ) : (
        <div className="procedure-plan-list">
          {visible.map((plan) => {
            const editing = Number(editingId) === Number(plan.id);
            return (
              <div
                key={plan.id}
                className={`procedure-plan-card procedure-${plan.status} ${
                  Number(focusProcedureId) === Number(plan.id) ? "procedure-focused" : ""
                }`}
              >
                <div className="procedure-plan-top">
                  <div>
                    <div className="procedure-plan-title">
                      Procedure #{plan.id}
                      <span className={`procedure-status status-${plan.status}`}>
                        {plan.status}
                      </span>
                    </div>
                    <div className="procedure-plan-meta">
                      <span><strong>Start:</strong> {fmtDate(plan.start_date)}</span>
                      <span><strong>Days:</strong> {plan.total_days}</span>
                      <span><strong>Completed:</strong> {plan.completed_count || 0}</span>
                      <span><strong>Pending:</strong> {plan.pending_count || 0}</span>
                    </div>
                  </div>

                  {plan.status === "active" && canEdit && (
                    <div className="procedure-plan-actions">
                      {!editing ? (
                        <button type="button" className="procedure-btn edit" onClick={() => beginEdit(plan)}>
                          <i className="fa fa-pencil"></i> Update
                        </button>
                      ) : (
                        <>
                          <button type="button" className="procedure-btn save" onClick={() => saveEdit(plan)}>
                            <i className="fa fa-save"></i> Save
                          </button>
                          <button type="button" className="procedure-btn light" onClick={() => setEditingId(null)}>
                            Cancel Edit
                          </button>
                        </>
                      )}
                      <button type="button" className="procedure-btn danger" onClick={() => cancelPlan(plan)}>
                        <i className="fa fa-ban"></i> Cancel Procedure
                      </button>
                    </div>
                  )}
                </div>

                {editing ? (
                  <div className="procedure-edit-grid">
                    <div>
                      <label>No. of Days</label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={editForm.total_days}
                        onChange={(e) => setEditForm((p) => ({ ...p, total_days: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label>Expected Start Date</label>
                      <input
                        type="date"
                        value={editForm.start_date}
                        onChange={(e) => setEditForm((p) => ({ ...p, start_date: e.target.value }))}
                      />
                    </div>
                    <div className="procedure-edit-details">
                      <label>Procedure / Instruction</label>
                      <textarea
                        rows="3"
                        value={editForm.procedure_details}
                        onChange={(e) =>
                          setEditForm((p) => ({ ...p, procedure_details: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="procedure-instruction">{plan.procedure_details}</div>
                )}

                {plan.cancellation_reason && (
                  <div className="procedure-cancel-reason">
                    <strong>Cancellation reason:</strong> {plan.cancellation_reason}
                  </div>
                )}

                <div className="procedure-session-list">
                  {(plan.sessions || []).map((session) => {
                    const draft = sessionDrafts[session.id] || {};
                    const pending = session.status === "pending";

                    return (
                      <div key={session.id} className={`procedure-session-row session-${session.status}`}>
                        <div className="procedure-day-box">Day {session.day_no}</div>

                        {pending ? (
                          <>
                            <div className="procedure-session-date">
                              <label>Date</label>
                              <input
                                type="date"
                                value={draft.actual_date || session.scheduled_date || ""}
                                onChange={(e) =>
                                  updateSessionDraft(session.id, "actual_date", e.target.value)
                                }
                              />
                              <small>Expected: {fmtDate(session.scheduled_date)}</small>
                            </div>

                            <div className="procedure-session-details">
                              <label>Procedure Details / Observation</label>
                              <textarea
                                rows="2"
                                value={draft.completion_details || ""}
                                onChange={(e) =>
                                  updateSessionDraft(
                                    session.id,
                                    "completion_details",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter what was done / injection / dressing / observation..."
                              />
                            </div>

                            <div className="procedure-session-actions">
                              {canComplete && (
                                <button
                                  type="button"
                                  className="procedure-btn complete"
                                  onClick={() => completeSession(session)}
                                >
                                  <i className="fa fa-check"></i> Complete
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  type="button"
                                  className="procedure-btn danger-outline"
                                  onClick={() => cancelSession(session)}
                                >
                                  Cancel Day
                                </button>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="procedure-session-date readonly">
                              <strong>
                                {session.status === "completed"
                                  ? fmtDate(session.actual_date || session.scheduled_date)
                                  : fmtDate(session.scheduled_date)}
                              </strong>
                              <span className={`procedure-session-status ${session.status}`}>
                                {session.status}
                              </span>
                            </div>

                            <div className="procedure-session-details readonly">
                              {session.status === "completed" ? (
                                <>
                                  <div>{session.completion_details || "-"}</div>
                                  <small>
                                    Completed by {session.completed_by || "-"}
                                    {session.completed_at ? ` • ${session.completed_at}` : ""}
                                  </small>
                                </>
                              ) : (
                                <>
                                  <div>{session.cancellation_reason || "Cancelled"}</div>
                                  <small>Cancelled by {session.cancelled_by || "-"}</small>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
